import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import {
  Card,
  Text,
  SearchResultsAdvanced,
  NotificationList,
  useResponsiveParams,
  Button,
  Paper,
  Grid,
  GridItem,
} from 'quanta-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faEnvelope,
  faExclamationTriangle,
  faUserInjured,
  faMobileAlt,
  faSearch,
  faSpinner,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

import {
  LOB_PRODUCT_LIST,
  DATE_RANGE,
  QUOTE_STATUS_LIST,
  SEARCH_QUOTES,
  INTERMEDIARY_LIST,
  VIEW_QUOTE_FIRE_SA,
  GET_FIRE_POLICY_VAR_DETAILS,
  // CHECKAUTH_QUERY,
} from '../queries';
import { Loading } from 'quanta-flows/modules/layout';
import { useFetchQuery } from 'quanta-flows';
import QuickSearchFire from './quick-search-fire';
import { updateFireQuote } from 'quanta-flows/modules/quote/fire/state';
import { initBuyFirePolicyVar } from 'quanta-flows/modules/proposal/fire/state';

const offset = 0;
const PAGE_SIZE = 4;

export default function SmartSearchPanelFire({
  alerts,
  setAlerts,
  voice,
  setCopyQuoteDisplay,
  setMoveToPlans,
}) {
  const { product_code: productCodeFromURL } = useParams();
  const [searchData, setSearchData] = useState([]);
  const [searchQuoteMeta, setSearchQuoteMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isIdle, setIdle] = useState(true);
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyFromQuoteNumber, setCopyFromQuoteNumber] = useState('');
  const reformquoteDetailsData = (val, returnType) => {
    return val && val.toString().trim() !== '' ? val : returnType;
  };
  
  const noOfColsMobile = 1,
    noOfColsDesktop = 3;

  const inputColSpans = useResponsiveParams({
    for_mobile: noOfColsMobile,
    for_desktop: noOfColsDesktop,
  });

  const screenType = useResponsiveParams({
    for_mobile: 'mobile',
    for_desktop: 'desktop',
  });

  const fetchQuery = useFetchQuery();

  useEffect(() => {
    initBuyFirePolicyVar();
  }, []);

  const { loading: loadingIntermediary, data: intermediaryData } =
    useQuery(INTERMEDIARY_LIST);

  const { loading: loadingLobDropdown, data: lobDropdownDetais } =
    useQuery(LOB_PRODUCT_LIST, {
      fetchPolicy: 'cache-first',
    });

  const { loading: loadingDateRange, data: dateRangeData } = useQuery(
    DATE_RANGE,
    {
      variables: {
        type: 'Quote',
      },
      fetchPolicy: 'cache-first',
    },
  );

  const {
    loading: loadingQuoteStatusDropdown,
    data: quoteStatusDropdown,
  } = useQuery(QUOTE_STATUS_LIST, {
    fetchPolicy: 'cache-first',
  });

  // const { loading: loadingRole, data: dataRole } = useQuery(
  //   CHECKAUTH_QUERY,
  //   {
  //     fetchPolicy: 'cache-first',
  //   },
  // );

  if (
    loadingLobDropdown ||
    loadingQuoteStatusDropdown ||
    loadingIntermediary ||
    loadingDateRange
    // loadingRole
  ) {
    return (
      <Card className="min-h-screen">
        <Loading />
      </Card>
    );
  }

  const { lobProductList } = lobDropdownDetais;
  const { dateRange } = dateRangeData;
  const { quoteStatusList } = quoteStatusDropdown;
  const { myIntermediaries } = intermediaryData;

  const initialValues = {
    intermediaryCode: myIntermediaries.find((imd) => imd.isSelected)
      ?.intermediaryCode,
    startDate: moment()
      .subtract(dateRange.min, 'months')
      .format('DD/MM/YYYY')
      .toString(),
    endDate: moment().format('DD/MM/YYYY').toString(),
    productCode: productCodeFromURL ? productCodeFromURL : '',
    customerName: '',
    customerMobile: '',
    quoteStatus: quoteStatusList[0].code,
    quoteNumber: '',
    lob: '',
    searchType: 'I have a Quotation Number',
    fuzzySearch: '',
  };

  const formValidate = (values) => {
    const errors = {};
    if (!values.intermediaryCode) {
      errors.intermediaryCode = '* Required';
    }
    if (
      !values.quoteNumber &&
      !values.customerName &&
      !values.customerMobile &&
      !advancedSearch
    ) {
      errors.fuzzySearch = '* Required';
    }
    if (advancedSearch) {
      if (!values.startDate || values.startDate === '') {
        errors.startDate = '* Required';
      } else if (!values.endDate || values.endDate === '') {
        errors.endDate = '* Required';
      }
      if (!values.productCode || values.productCode === '') {
        errors.productCode = '* Required';
      }
    }

    if (values.startDate !== '' && values.endDate !== '') {
      if (
        moment(values.endDate, 'DD/MM/YYYY').diff(
          moment(values.startDate, 'DD/MM/YYYY'),
          'months',
        ) > dateRange.max
      ) {
        errors.endDate = `* Date Range cannot be greater than ${dateRange.max} months`;
      }
      if (
        moment(values.endDate, 'DD/MM/YYYY').isBefore(
          moment(values.startDate, 'DD/MM/YYYY'),
        )
      ) {
        errors.endDate = '* To Date cannot be before From Date';
      }
    }
    if (values?.customerName) {
      if (values.customerName.match(/^[0-9]+$/)) {
        errors.customerName = '* Customer Name cannot be all numeric';
      } else if (values.customerName.match(/^[_@!$%*^()=./#&+-]*$/)) {
        errors.customerName =
          '* Customer Name cannot be all special characters';
      } else if (values.customerName.match(/\d/)) {
        errors.customerName =
          '* Customer Name cannot be alphanumeric';
      }
    }
    return errors;
  };

  const searchInput = {
    flow: 'cols',
    repeat: inputColSpans,
    gapX: 8,
    gapY: 1,
    // title: 'Search Quotes',
    data: [
      ...(!advancedSearch
        ? [
            {
              type: 'render',
              size: 'full',
              colSpan: 4,
              component: (values, setFieldValue, resetForm) => (
                <QuickSearchFire
                  {...{
                    myIntermediaries,
                    setAdvancedSearch,
                    loading,
                    values,
                    setFieldValue,
                    voice,
                    disabled: copying,
                  }}
                />
              ),
            },
          ]
        : [
            {
              type: 'select',
              size: 'full',
              label: (
                <div>
                  Choose Intermediary <Text severity="error">*</Text>
                </div>
              ),
              name: 'intermediaryCode',
              disableSelect: true,
              disabled: copying,
              data: myIntermediaries
                ? [
                    ...myIntermediaries.map((intermediaryCode) => {
                      return {
                        label: intermediaryCode.intermediaryName
                          ? intermediaryCode.intermediaryName
                          : '',
                        value: intermediaryCode.intermediaryCode
                          ? intermediaryCode.intermediaryCode
                          : '',
                      };
                    }),
                  ]
                : [],
            },
            {
              type: 'date',
              size: 'full',
              label: (
                <div>
                  From Date <Text severity="error">*</Text>
                </div>
              ),
              name: 'startDate',
              scrollStopWhenCalendarOpen: false,
              disabled: copying,
            },
            {
              type: 'date',
              size: 'full',
              label: (
                <div>
                  To Date <Text severity="error">*</Text>
                </div>
              ),
              name: 'endDate',
              scrollStopWhenCalendarOpen: false,
              disabled: copying,
            },
            {
              type: 'text',
              size: 'full',
              label: (
                <div>
                  Customer Name <Text severity="error"></Text>
                </div>
              ),
              name: 'customerName',
              disabled: copying,
            },
            {
              type: 'select',
              size: 'large',
              mega: true,
              label: (
                <div>
                  Product <Text severity="error">*</Text>
                </div>
              ),
              name: 'productCode',
              disabled: true,
              data: lobProductList?.map((lob) => {
                return {
                  category: lob.lob_name,
                  values: lob.products.map((product) => {
                    return {
                      label: product.product_name,
                      value: product.product_code,
                    };
                  }),
                };
              }),
            },
            {
              type: 'select',
              size: 'full',
              label: (
                <div>
                  Quote Status <Text severity="error"></Text>
                </div>
              ),
              name: 'quoteStatus',
              disabled: copying,
              disableSelect: true,
              data: quoteStatusList
                ? [
                    ...quoteStatusList.map((quoteStatus) => {
                      return {
                        label: quoteStatus.value
                          ? quoteStatus.value
                          : '',
                        value: quoteStatus.code
                          ? quoteStatus.code
                          : '',
                      };
                    }),
                  ]
                : [],
            },
            // lobProductList
            //   ?.find((lob) =>
            //     lob?.products?.find(
            //       (product) =>
            //         product?.product_code
            //           ?.toString()
            //           ?.toLowerCase() ===
            //         productCode?.toString()?.toLowerCase(),
            //     ),
            //   )
            //   ?.lob_name?.toString()
            //   ?.toLowerCase() === 'motor'
            //   ? {
            //       type: 'input-wrapper',
            //       size: 'full',
            //       fields: [
            //         'registrationNumberParts.part1',
            //         'registrationNumberParts.part2',
            //         'registrationNumberParts.part3',
            //         'registrationNumberParts.part4',
            //       ],
            //       children: (
            //         <>
            //           <QuantaInput
            //             name="registrationNumberParts.part1"
            //             maxLength="2"
            //             size="full"
            //             type="text"
            //             placeholder="MH"
            //             noerror
            //             className="uppercase"
            //             onKeyUp={(e) => {
            //               if (e.target.value.length === 2) {
            //                 document
            //                   .getElementsByName(
            //                     'registrationNumberParts.part2',
            //                   )
            //                   .forEach((e) => e.focus());
            //               }
            //             }}
            //             onDataChange={(val, setFieldValue) => {
            //               setFieldValue(
            //                 `registrationNumberParts.part1`,
            //                 val?.toString()?.toUpperCase(),
            //               );
            //             }}
            //           />
            //           <Text
            //             className={`flex items-center font-bold h-12`}
            //           >
            //             -
            //           </Text>
            //           <QuantaInput
            //             name="registrationNumberParts.part2"
            //             maxLength="2"
            //             size="full"
            //             type="text"
            //             placeholder="01"
            //             noerror
            //             onKeyUp={(e) => {
            //               if (e.target.value.length === 2) {
            //                 document
            //                   .getElementsByName(
            //                     'registrationNumberParts.part3',
            //                   )
            //                   .forEach((e) => e.focus());
            //               }
            //             }}
            //           />
            //           <Text
            //             className={`flex items-center font-bold h-12`}
            //           >
            //             -
            //           </Text>
            //           <QuantaInput
            //             name="registrationNumberParts.part3"
            //             maxLength="2"
            //             size="full"
            //             type="text"
            //             placeholder="CR"
            //             noerror
            //             className="uppercase"
            //             onKeyUp={(e) => {
            //               if (e.target.value.length === 2) {
            //                 document
            //                   .getElementsByName(
            //                     'registrationNumberParts.part4',
            //                   )
            //                   .forEach((e) => e.focus());
            //               }
            //             }}
            //             onDataChange={(val, setFieldValue) => {
            //               setFieldValue(
            //                 `registrationNumberParts.part3`,
            //                 val?.toString()?.toUpperCase(),
            //               );
            //             }}
            //           />
            //           <Text
            //             className={`flex items-center font-bold h-12`}
            //           >
            //             -
            //           </Text>
            //           <QuantaInput
            //             name="registrationNumberParts.part4"
            //             maxLength="4"
            //             size="full"
            //             type="text"
            //             placeholder="2845"
            //             noerror
            //           />
            //         </>
            //       ),
            //       label: (
            //         <Text type={'label'}>
            //           Vehicle Registration Number
            //         </Text>
            //       ),
            //       name: 'registrationNumber',
            //     }
            //   : null,
            {
              type: 'render',
              colSpan: inputColSpans,
              component: (values, setFieldValue, resetForm) => {
                return (
                  <div
                    className={`flex ${
                      screenType?.toString()?.toLowerCase() ===
                      'mobile'
                        ? 'flex-col space-y-2 justify-between items-center'
                        : 'space-x-2 justify-end'
                    } mb-4`}
                  >
                    <Text
                      className={
                        screenType?.toString()?.toLowerCase() ===
                        'mobile'
                          ? 'w-full'
                          : ''
                      }
                    >
                      <Button
                        flat={true}
                        type="button"
                        className={`w-${
                          screenType?.toString()?.toLowerCase() ===
                          'mobile'
                            ? 'full'
                            : '40'
                        } border-0`}
                        onClick={(_) => setAdvancedSearch(false)}
                        disabled={copying}
                      >
                        Quick Search
                      </Button>
                    </Text>
                    <Text
                      className={
                        screenType?.toString()?.toLowerCase() ===
                        'mobile'
                          ? 'w-full'
                          : ''
                      }
                    >
                      <Button
                        flat={true}
                        type="button"
                        className={`w-${
                          screenType?.toString()?.toLowerCase() ===
                          'mobile'
                            ? 'full'
                            : '40'
                        }`}
                        disabled={copying}
                        onClick={(_) => {
                          const otherFormValues = {
                            intermediaryCode:
                              values?.intermediaryCode,
                            quoteNumber: values?.quoteNumber,
                            searchType: values?.searchType,
                          };
                          resetForm(initialValues);
                          Object.keys(otherFormValues).forEach(
                            (k) => {
                              setFieldValue(k, otherFormValues[k]);
                            },
                          );
                        }}
                      >
                        Reset
                      </Button>
                    </Text>
                    <Text
                      className={
                        screenType?.toString()?.toLowerCase() ===
                        'mobile'
                          ? 'w-full'
                          : ''
                      }
                    >
                      <Button
                        flat={false}
                        type="submit"
                        className={`w-${
                          screenType?.toString()?.toLowerCase() ===
                          'mobile'
                            ? 'full'
                            : '40'
                        }`}
                        disabled={loading || copying}
                        iconPre={
                          loading ? (
                            <FontAwesomeIcon
                              icon={faSpinner}
                              spin={true}
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faSearch}
                              className="mx-2"
                            />
                          )
                        }
                      >
                        Search
                      </Button>
                    </Text>
                  </div>
                );
              },
            },
          ]?.filter((d) => d)),
    ],
    initialValues,
    validate: formValidate,
    onSearch: async (values) => {
      try {
        setSearchData([]);
        setAlerts([]);
        let { lob, ...requestBody } = values;
        requestBody.startDate = moment(
          requestBody.startDate,
          'DD-MM-YYYY',
        ).format('YYYY-MM-DD');
        requestBody.endDate = moment(
          requestBody.endDate,
          'DD-MM-YYYY',
        ).format('YYYY-MM-DD');

        let queryOutput = fetchQuery({
          query: SEARCH_QUOTES,
          variables: advancedSearch
            ? {
                filter: {
                  intermediaryCode: values?.intermediaryCode,
                  productCode: values?.productCode,
                  customerName: values?.customerName,
                  startDate: requestBody?.startDate,
                  endDate: requestBody?.endDate,
                  quoteStatus: values?.quoteStatus,
                  isFuzzySearch: false,
                },
              }
            : values?.customerName
            ? {
                filter: {
                  intermediaryCode: values?.intermediaryCode,
                  customerName: values?.customerName,
                  productCode: values?.productCode,
                  isFuzzySearch: true,
                },
              }
            : values?.customerMobile
            ? {
                filter: {
                  intermediaryCode: values?.intermediaryCode,
                  mobileNumber: values?.customerMobile,
                  productCode: values?.productCode,
                  isFuzzySearch: true,
                },
              }
            : {
                filter: {
                  intermediaryCode: values?.intermediaryCode,
                  quoteNumber: values?.quoteNumber,
                  productCode: values?.productCode,
                  isFuzzySearch: true,
                },
              },
          fetchPolicy: 'network-only',
        });
        setLoading(true);
        let { data } = await queryOutput;
        setLoading(false);
        if (
          data.searchQuotes.length === 0 &&
          data._quote_meta.metaMessage
        ) {
          setAlerts([
            {
              header: data._quote_meta.metaMessage.header,
              id: 1,
              content: (
                <div className="flex flex-row flex-wrap space-y-2 w-full h-full">
                  <span>{data._quote_meta.metaMessage.message}</span>
                </div>
              ),
              type: data._quote_meta.metaMessage.type,
              timeout: null,
              disableClose: false,
            },
          ]);
          //   setCardSummaryDetail(undefined);
        }
        // if (data.searchQuotes.length === 1 && systemConfig?.searchRedirect === true) {
        //   history.push(
        //     `/quote/saved/${data?.searchQuotes[0]?.lob?.lobCode}/${data?.searchQuotes[0]?.lob?.lobName}/${data?.searchQuotes[0]?.product?.productCode}/${data?.searchQuotes[0]?.product?.productName}/${data.searchQuotes[0].quoteNumber}`,
        //   );
        // }
        //setSearchData(data?.searchQuotes?.filter((quote)=>quote?.product?.productCode?.toString()?.toLowerCase() === productCodeFromURL?.toString()?.toLowerCase()));
        setSearchData(data.searchQuotes);
        setSearchQuoteMeta(data._quote_meta);
        setIdle(false);
      } catch (error) {
        // setAlerts([
        //   {
        //     header: 'Quotes fetch failed',
        //     id: 1,
        //     content: (
        //       <div className="flex flex-row flex-wrap space-y-2 w-full h-full">
        //         <span>{error.message}</span>
        //       </div>
        //     ),
        //     type: 'error',
        //     timeout: null,
        //     disableClose: false,
        //   },
        // ]);
        setAlerts([
          {
            header: 'No Data Found',
            id: 1,
            content: (
              <div className="flex flex-row flex-wrap space-y-2 w-full h-full">
                <span>
                  No search results were found. Refine the input
                  criteria
                </span>
              </div>
            ),
            type: 'error',
            timeout: null,
            disableClose: false,
          },
        ]);
        setLoading(false);
        setIdle(false);
      }
    },
  };

  let searchResults = {
    data: searchData,
    onIdle: () => {
      if (loading) return <Loading />;
      else if (isIdle) return <></>;
      return undefined;
    },
    columns: [
      {
        Header: 'Customer Name',
        Cell: ({ row }) => {
          return (
            <div className="flex flex-col justify-center break-normal max-w-xs">
              <span>
                {row.original?.customer?.org_detail?.org_name
                  ? row.original?.customer?.org_detail?.org_name
                  : row.original?.customer?.indv_detail?.full_name
                  ? row.original?.customer?.indv_detail?.full_name
                  : [
                      row.original?.customer?.indv_detail?.first_name?.trim(),
                      row.original?.customer?.indv_detail?.middle_name?.trim(),
                      row.original?.customer?.indv_detail?.surname?.trim(),
                    ].join(' ')}
              </span>
            </div>
          );
        },
      },
      {
        Header: 'Contact',
        Cell: ({ row }) => {
          return (
            <div className="flex flex-col justify-center">
              <span>
                {row.original.customer &&
                row.original.customer.contact &&
                row.original.customer.contact.email
                  ? row.original.customer.contact.email
                  : null}
              </span>
              <span>
                {row.original.customer &&
                row.original.customer.contact &&
                row.original.customer.contact.mobile
                  ? row.original.customer.contact.mobile
                  : null}
              </span>
            </div>
          );
        },
      },
      {
        Header: 'Quote Reference Number',
        accessor: 'quoteNumber',
      },
      {
        Header: 'Quote Status / Saved On',
        Cell: ({ row }) => {
          return (
            <div className="flex flex-col justify-center">
              <span>{row.original?.quoteStatus}</span>
              <span>{row.original?.quoteCreationDate}</span>
            </div>
          );
        },
      },
      {
        Header: ' ',
        Cell: ({ row }) => {
          return (
            <Text className="flex justify-end w-full">
              <Button
                type={'button'}
                flat={false}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const fetchQuoteDetails = fetchQuery({
                      query: VIEW_QUOTE_FIRE_SA,
                      variables: {
                        input: {
                          quoteNumber: row?.original?.quoteNumber,
                        },
                      },
                    });
                    const getFireQuoteVar = fetchQuery({
                      query: GET_FIRE_POLICY_VAR_DETAILS,
                    });
                    setAlerts([]);
                    setCopyFromQuoteNumber(
                      row?.original?.quoteNumber,
                    );
                    setCopying(true);
                    const { data: quoteDetailsData } =
                      await fetchQuoteDetails;
                    const { data: fireReactiveVarData } =
                      await getFireQuoteVar;
                    const { fireQuote } = fireReactiveVarData;
                    setCopying(false);
                    updateFireQuote({
                      currentTab: 2,
                      captureMode: 'create',
                      quotationType:
                        quoteDetailsData?.viewQuoteFireSA
                          ?.quotationType,
                      isPremiumCalculating: false,
                      coverData: {
                        addOnCovers: quoteDetailsData?.viewQuoteFireSA?.riskCovers,
                        rawRiskData: [],
                      },
                      riskcoverdata:quoteDetailsData?.viewQuoteFireSA?.riskCovers,
                      tabDetails: {
                        customerTabDetails: {
                          previousClaimExperienceDetails: {
                            isClaimMadeInPreviousYears:
                              quoteDetailsData?.viewQuoteFireSA?.prevClaimExpDtls
                                ?.anyPrevClaimsExp === true
                                ? 'Yes'
                                : 'No',
                            claimInPreviousYearsInfo: quoteDetailsData?.viewQuoteFireSA
                              ?.prevClaimExpDtls?.prevClaimExpDesc
                              ? `${quoteDetailsData?.viewQuoteFireSA?.prevClaimExpDtls?.prevClaimExpDesc}`
                              : '',
                          },
                          customerDetails: {
                            customer: {
                              id: quoteDetailsData?.viewQuoteFireSA?.customer?.cust_number,
                              customerType:
                                quoteDetailsData?.viewQuoteFireSA?.customer?.cust_type?.code,
                              orgName: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.org_detail
                                  ?.org_name,
                                '',
                              ),
                              orgType: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.org_detail
                                  ?.org_type?.code,
                                '',
                              ),
                              contactName: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.org_detail
                                  ?.contact_name,
                                '',
                              ),
                              title: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail?.title
                                ?.code,
                                '',
                              ),
                              gender: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.gender?.code,
                                '',
                              ),
                              proposerDOB: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.date_of_birth,
                                '',
                              ),
                            firstName: reformquoteDetailsData(
                              quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                ?.first_name,
                              '',
                            ),
                            middleName: reformquoteDetailsData(
                              quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                ?.middle_name,
                              '',
                            ),
                            fullName: reformquoteDetailsData(
                              quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                ?.full_name,
                              '',
                            ),
                            lastName: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.surname,
                              '',
                            ),
                              nationality: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.nationality?.code,
                                '',
                              ),
                              occupation: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.occupation?.code,
                                '',
                              ),
                              maritalStatus: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.marital_status?.code,
                                '',
                              ),
                              panNumber: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.pan_card_number,
                                '',
                              ),
                              idProofType: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.id_proof_type?.code,
                                '',
                              ),
                              idProofNumber: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                  ?.id_proof_number,
                                '',
                              ),
                              permAddressLine1: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.address_line1,
                                '',
                              ),
                              permAddressLine2: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.address_line2,
                                '',
                              ),
                              permAddressLine3: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.address_line3,
                                '',
                              ),
                              permLandmark: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.land_mark,
                                '',
                              ),
                              permPincode: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.pin_code,
                                '',
                              ),
                              permCityTownVillage: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.city,
                                '',
                              ),
                              permDistrict: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.district,
                                '',
                              ),
                              permState: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.permanent_address
                                  ?.state,
                                '',
                              ),
                              permCommAddressYN: quoteDetailsData?.viewQuoteFireSA?.customer
                                ?.comm_equal_perm
                                ? true
                                : false,
                              commAddressLine1: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.address_line1,
                                '',
                              ),
                              commAddressLine2: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.address_line2,
                                '',
                              ),
                              commAddressLine3: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.address_line3,
                                '',
                              ),
                              commLandmark: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.land_mark,
                                '',
                              ),
                              commPincode: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.pin_code,
                                '',
                              ),
                              commCityTownVillage: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.city,
                                '',
                              ),
                              commDistrict: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.district,
                                '',
                              ),
                              commState: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer
                                  ?.communication_address?.state,
                                '',
                              ),
                              emailID: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.contact?.email,
                                '',
                              ),
                              mobileNo: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.contact?.mobile,
                                '',
                              ),
                              phoneNo: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.contact?.telephone,
                                '',
                              ),
                              faxNo: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.contact
                                  ?.fax_number,
                                '',
                              ),
                              altPhoneNo: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.contact
                                  ?.alt_telephone,
                                '',
                              ),
                              altMobileNo: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.contact
                                  ?.alt_mobile,
                                '',
                              ),
                              regNumber: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.org_detail
                                  ?.reg_number,
                                '',
                              ),
                              tanNumber: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.org_detail
                                  ?.tan_number,
                                '',
                              ),
                              gstNumber: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.org_detail
                                  ?.gst_number ||
                                  quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                                    ?.gst_number,
                                '',
                              ),
                            },
                            customerMeta: {
                              crn: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.cust_ref_number,
                                '',
                              ),
                              isNewCustomer: quoteDetailsData?.viewQuoteFireSA?.customer
                                ?.cust_number
                                ? false
                                : true,
                              ifscCode: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.bank_detail
                                  ?.ifsc_code,
                                '',
                              ),
                              bankName: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.bank_detail?.name,
                                '',
                              ),
                              branchName: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.bank_detail
                                  ?.branch_name,
                                '',
                              ),
                              bankAcno: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.bank_detail
                                  ?.acc_number,
                                '',
                              ),
                              accountHolderName: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.bank_detail
                                  ?.acc_holder,
                                '',
                              ),
                              micrCode: reformquoteDetailsData(
                                quoteDetailsData?.viewQuoteFireSA?.customer?.bank_detail
                                  ?.micr_code,
                                '',
                              ),
                            },
                            // customerType: {
                            //   code: quoteDetailsData?.viewQuoteFireSA?.customer?.cust_type
                            //     ?.code,
                            //   value:
                            //     quoteDetailsData?.viewQuoteFireSA?.customer?.cust_type?.value,
                            // },
                            // firstName: reformquoteDetailsData(
                            //   quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                            //     ?.first_name,
                            //   '',
                            // ),
                            // middleName: reformquoteDetailsData(
                            //   quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                            //     ?.middle_name,
                            //   '',
                            // ),
                            // fullName: reformquoteDetailsData(
                            //   quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail
                            //     ?.full_name,
                            //   '',
                            // ),
                            // lastName: reformquoteDetailsData(
                            //   quoteDetailsData?.viewQuoteFireSA?.customer?.indv_detail?.surname,
                            //   '',
                            // ),
                            businessOfInsured: quoteDetailsData?.viewQuoteFireSA
                              ?.businessOfInsured
                              ? `${quoteDetailsData?.viewQuoteFireSA?.businessOfInsured}`
                              : '',
                            // orgInsuredName: reformquoteDetailsData(
                            //   quoteDetailsData?.viewQuoteFireSA?.customer?.org_detail?.org_name,
                            //   '',
                            //),
                          },
                          
                          policyTypeDetails: {
                            isInsuranceForKutchaConstruction:
                              quoteDetailsData?.viewQuoteFireSA?.isKutchaConstruct === true
                                ? 'Yes'
                                : 'No',
                                geographicalArea:quoteDetailsData?.viewQuoteFireSA?.district
                                ?`${quoteDetailsData?.viewQuoteFireSA?.district}`
                                :'',
                            policyOrCoverType: {
                              code: quoteDetailsData?.viewQuoteFireSA?.policyType
                                ? `${quoteDetailsData?.viewQuoteFireSA?.policyType}`
                                : '',
                              value: quoteDetailsData?.viewQuoteFireSA?.policyType
                                ? `${quoteDetailsData?.viewQuoteFireSA?.policyType}`
                                : '',
                            },
                            tenure: {
                              code: quoteDetailsData?.viewQuoteFireSA?.tenure?.code,
                              value: quoteDetailsData?.viewQuoteFireSA?.tenure?.value,
                            },
                            policyStartDate: quoteDetailsData?.viewQuoteFireSA?.policyStartDate
                              ? `${quoteDetailsData?.viewQuoteFireSA?.policyStartDate}`
                              : '',
                            policyExpiryDate: quoteDetailsData?.viewQuoteFireSA
                              ?.policyExpiryDate
                              ? `${quoteDetailsData?.viewQuoteFireSA?.policyExpiryDate}`
                              : '',
                          },
                        },
                        riskLocationTabDetails: {
                          nonFloaterType:
                            quoteDetailsData?.viewQuoteFireSA?.policyType
                              ?.toString().includes('Home') 
                              //=== 'Home Building And Home Contents'
                              ? {
                                  occupancy: {
                                    code: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                      ?.occupancyCodeValue?.code
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.code}`
                                      : '',
                                    value: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                      ?.occupancyCodeValue?.value
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.value}`
                                      : '',
                                    occupancySection: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.occupancySection
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.occupancySection}`
                                      : '',
                                    occupancyRate: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.occupancyRate
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.occupancyRate}`
                                      : '',
                                    riskCode: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.riskCode
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskCode}`
                                      : '',
                                    rateCode: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.rateCode
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.rateCode}`
                                      : '',
                                    riskGrade: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.riskGrade
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskGrade}`
                                      : '',
                                    riskGradeDefinition: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.riskGradeDefinition
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskGradeDefinition}`
                                      : '',
                                    riskPreference: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.riskPreference
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskPreference}`
                                      : '',
                                    gicClass: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.gicClass
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.gicClass}`
                                      : '',
                                    stfiRate: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.stfiRate
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.stfiRate}`
                                      : '',
                                    rsmdRate: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.occupancyCodeValue
                                      ?.rsmdRate
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.rsmdRate}`
                                      : '',
                                  },
                                  riskSumInsured: {
                                    costOfConstruction: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.costOfConstruction
                                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.costOfConstruction
                                                      :'',
                                    carpetArea: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.carpetArea
                                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.carpetArea
                                                      :'',
                                    buildingValue: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.buildingValue
                                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.buildingValue
                                                      :'',
                                    additionalStructure: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.additionalStructure
                                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.additionalStructure
                                                      :'',
                                    totalBuildingSI: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.totalBuildingSI
                                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.totalBuildingSI
                                                      :'',
                                  },
                                  stock: {
                                    generalContentSI: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.generalContentSI
                                    ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.generalContentSI
                                    :'',
                                    additionalContent: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.additionalContentSI
                                    ? 'Yes'
                                    : 'No',
                                    additionalContentSI: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.additionalContentSI
                                    ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.additionalContentSI
                                    :'',
                                    valuableContent: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.valuableContentSI
                                    ? 'Yes'
                                    : 'No',
                                    valuableContentSI: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.valuableContentSI
                                    ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.valuableContentSI
                                    :'',
                                    totalContentSI: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.totalContentSI
                                    ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.totalContentSI
                                    :'',
                                    totalSumInsured: quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.totalSumInsured
                                    ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.totalSumInsured
                                    :'',
                                  },
                                  riskCovers: [],
                                  addressDetails: {
                                    addressLine1: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.riskLocAddress
                                      ?.address_line1
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.riskLocAddress?.address_line1}`
                                      : '',
                                    pincode: quoteDetailsData?.viewQuoteFireSA?.pincode
                                    ?`${quoteDetailsData?.viewQuoteFireSA?.pincode}`
                                      : '',
                                    state: quoteDetailsData?.viewQuoteFireSA?.state
                                    ?`${quoteDetailsData?.viewQuoteFireSA?.state}`
                                      : '',
                                    eqZone: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.riskLocAddress
                                      ?.eqZone
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.riskLocAddress?.eqZone}`
                                      : '',
                                    district: quoteDetailsData?.viewQuoteFireSA?.district
                                    ?`${quoteDetailsData?.viewQuoteFireSA?.district}`
                                      : '',
                                    isSamePremise: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.riskLocAddress
                                      ?.isSamePremises
                                      ? true
                                      : false,
                                    locationCode: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.riskLocAddress
                                      ?.locationCode
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.riskLocAddress?.locationCode}`
                                      : '',
                                    locationDesc: '',
                                  },
                                  riskLocationID:
                                    quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                      ?.riskLocationID,
                                  floaterLocations:
                                    quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                      ?.floaterLocations?.length > 0
                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.floaterLocations?.map(
                                          (floaterLocation) => {
                                            return {
                                              pinCode: floaterLocation?.pin_code,
                                              state: floaterLocation?.state,
                                              city: floaterLocation?.city,
                                              district: floaterLocation?.district,
                                              eqZone: floaterLocation?.eqZone,
                                              addressLine1:
                                                floaterLocation?.address_line1,
                                              locationCode:
                                                floaterLocation?.locationCode,
                                              showEdit: true,
                                              // addressLine2: floaterLocation?.addressLine2,
                                              // addressLine3: floaterLocation?.addressLine3,
                                              // landmark: floaterLocation?.landmark,
                                            };
                                          },
                                        )
                                      : [],
                                  // riskSumInsured: {
                                  //   stocksSI:
                                  //     quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                  //       ?.stockSI,
                                  //   stocksInProcessSI:
                                  //     quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                  //       ?.stocksInProcessSI,
                                  //   stocksOnFloaterBasis: 'Yes',
                                  //   maximumStockSIAtOneLocation:
                                  //     quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                  //       ?.maxStockSIAtOneLoc,
                                  // },
                                  fireExtinguisherAppliancesDetails:
                                    quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.fireExtinguishApplisDtls?.map(
                                      (fireExtinguisherDtl) => {
                                        return {
                                          label:
                                            fireExtinguisherDtl?.applianceValue
                                              ? fireExtinguisherDtl?.applianceValue
                                              : '',
                                          value:
                                            fireExtinguisherDtl?.applianceCode
                                              ? fireExtinguisherDtl?.applianceCode
                                              : '',
                                          selected:
                                            fireExtinguisherDtl?.isSelected
                                              ? true
                                              : false,
                                        };
                                      },
                                    ),
                                  otherDetails: {
                                    classOfConstruction: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.constructionClass
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.constructionClass}`
                                      : '',
                                    distanceFromWaterbody: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.waterbodyDistance
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.waterbodyDistance}`
                                      : '',
                                    securityProvisions: quoteDetailsData?.viewQuoteFireSA
                                      ?.riskLocations[0]?.securityProvisions
                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.securityProvisions}`
                                      : '',
                                  },
                                  loadingAndDiscount: {
                                    discountPercentage:
                                      quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                        ?.discountPct,
                                    loadingPercentage:
                                      quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                        ?.loadingPct,
                                  },
                                  previousClaimDetails: {
                                    claimExperience:
                                      quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                        ?.prevClaimExpDesc,
                                  },
                                  locationPremium: {
                                    locationNetPremium:
                                      quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                        ?.premium?.locationNetPremium,
                                    locationGST:
                                      quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                        ?.premium?.locationGST,
                                    locationTotalPremium:
                                      quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                        ?.premium?.locationTotalPremium,
                                    stampDuty:
                                      quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]
                                        ?.premium?.stampDuty,
                                  },
                                }
                              : {
                                  occupancy: {
                                    code: '',
                                    value: '',
                                    occupancySection: '',
                                    occupancyRate: '',
                                    riskCode: '',
                                    rateCode: '',
                                    riskGrade: '',
                                    riskGradeDefinition: '',
                                    riskPreference: '',
                                    gicClass: '',
                                    stfiRate: '',
                                    rsmdRate: '',
                                  },
                                  riskLocationID: '',
                                  floaterLocations: [],
                                  riskSumInsured: {
                                    stocksSI: '',
                                    stocksInProcessSI: '',
                                    stocksOnFloaterBasis: '',
                                    maximumStockSIAtOneLocation: '',
                                  },
                                  fireExtinguisherAppliancesDetails: [],
                                  otherDetails: {
                                    classOfConstruction: '',
                                    distanceFromWaterbody: '',
                                    securityProvisions: '',
                                  },
                                  loadingAndDiscount: {
                                    discountPercentage: '',
                                    loadingPercentage: '',
                                  },
                                  previousClaimDetails: {
                                    claimExperience: '',
                                  },
                                  locationPremium: {
                                    locationNetPremium: '',
                                    locationGST: '',
                                    locationTotalPremium: '',
                                    stampDuty: '',
                                  },
                                },
                          FloaterType:
                            quoteDetailsData?.viewQuoteFireSA?.policyType
                              ?.toString() === 'floater'
                              ? quoteDetailsData?.viewQuoteFireSA?.riskLocations?.map(
                                  (_, idx) => {
                                    return {
                                      riskLocationID:
                                        quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                          ?.riskLocationID,
                                      isDeleted: false,
                                      occupancy: {
                                        code: quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                          idx
                                        ]?.occupancyCodeValue?.code
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.code}`
                                          : '',
                                        value: quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                          idx
                                        ]?.occupancyCodeValue?.value
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.value}`
                                          : '',
                                        occupancySection: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.occupancySection
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.occupancySection}`
                                          : '',
                                        occupancyRate: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.occupancyRate
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.occupancyRate}`
                                          : '',
                                        riskCode: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.riskCode
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskCode}`
                                          : '',
                                        rateCode: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.rateCode
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.rateCode}`
                                          : '',
                                        riskGrade: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.riskGrade
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskGrade}`
                                          : '',
                                        riskGradeDefinition: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.riskGradeDefinition
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskGradeDefinition}`
                                          : '',
                                        riskPreference: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.riskPreference
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskPreference}`
                                          : '',
                                        gicClass: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.gicClass
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.gicClass}`
                                          : '',
                                        stfiRate: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.stfiRate
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.stfiRate}`
                                          : '',
                                        rsmdRate: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.occupancyCodeValue
                                          ?.rsmdRate
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.rsmdRate}`
                                          : '',
                                      },
                                      isLocationSaved: true,
                                      riskSumInsured: {
                                        buildingSI:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.buildingSI,
                                        plantMachinerySI:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.plantMachinerySI,
                                        furnitureFixtureSI:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.furnitureFixtureSI,
                                        rawMaterialSI:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.rawMaterialSI,
                                        otherContentsSI:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.otherContentsSI,
                                      },
                                      stock: {
                                        stocksSI:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.stockSI,
                                        stocksInProcessSI:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.stocksInProcessSI,
                                        stocksOnFloaterBasis:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.stockOnFloaterBasis === true
                                            ? 'Yes'
                                            : 'No',
                                        maximumStockSIAtOneLocation:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.maxStockSIAtOneLoc,
                                      },
                                      locationSumInsured:
                                        quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                          ?.locationSI,
                                      fireExtinguisherAppliancesDetails:
                                        quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                          idx
                                        ]?.fireExtinguishApplisDtls?.map(
                                          (fireExtinguisherDtl) => {
                                            return {
                                              label:
                                                fireExtinguisherDtl?.applianceValue
                                                  ? fireExtinguisherDtl?.applianceValue
                                                  : '',
                                              value:
                                                fireExtinguisherDtl?.applianceCode
                                                  ? fireExtinguisherDtl?.applianceCode
                                                  : '',
                                              selected:
                                                fireExtinguisherDtl?.isSelected
                                                  ? true
                                                  : false,
                                            };
                                          },
                                        ),
                                      otherDetails: {
                                        classOfConstruction: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.constructionClass
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.constructionClass}`
                                          : '',
                                        distanceFromWaterbody: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.waterbodyDistance
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.waterbodyDistance}`
                                          : '',
                                        securityProvisions: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.securityProvisions
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.securityProvisions}`
                                          : '',
                                      },
                                      loadingAndDiscount: {
                                        discountPercentage:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.discountPct,
                                        loadingPercentage:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.loadingPct,
                                      },
                                      locationPremium: {
                                        locationNetPremium:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.premium?.locationNetPremium,
                                        locationGST:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.premium?.locationGST,
                                        locationTotalPremium:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.premium?.locationTotalPremium,
                                        stampDuty:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.premium?.stampDuty,
                                      },
                                      previousClaimDetails: {
                                        claimExperience:
                                          quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                            ?.prevClaimExpDesc,
                                      },
                                      addressDetails: {
                                        addressLine1: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.riskLocAddress
                                          ?.address_line1
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.address_line1}`
                                          : '',
                                        pincode: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.riskLocAddress
                                          ?.pin_code
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.pin_code}`
                                          : '',
                                        state: quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                          idx
                                        ]?.riskLocAddress?.state
                                          ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                              idx
                                            ]?.riskLocAddress?.state
                                          : '',
                                        eqZone: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.riskLocAddress
                                          ?.eqZone
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.eqZone}`
                                          : '',
                                        district: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.riskLocAddress
                                          ?.district
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.district}`
                                          : '',
                                        isSamePremise: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.riskLocAddress
                                          ?.isSamePremises
                                          ? true
                                          : false,
                                        locationCode: quoteDetailsData?.viewQuoteFireSA
                                          ?.riskLocations[idx]?.riskLocAddress
                                          ?.locationCode
                                          ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.locationCode}`
                                          : '',
                                        locationDesc: '',
                                      },
                                      stockLocations:
                                        quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]
                                          ?.stockOnFloaterBasis === true
                                          ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                              idx
                                            ]?.floaterLocations?.length > 0
                                            ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                                idx
                                              ]?.floaterLocations?.map(
                                                (floaterLocation) => {
                                                  return {
                                                    isDeleted: false,
                                                    pincode:
                                                      floaterLocation?.pin_code,
                                                    state: floaterLocation?.state,
                                                    city: floaterLocation?.city,
                                                    district:
                                                      floaterLocation?.district,
                                                    eqZone:
                                                      floaterLocation?.eqZone,
                                                    addressLine1:
                                                      floaterLocation?.address_line1,
                                                    locationCode:
                                                      floaterLocation?.locationCode,
                                                  };
                                                },
                                              )
                                            : []
                                          : [],
                                    };
                                  },
                                )
                              : [],
                        },
                        additionalTabDetails: {
                          clauses: {
                            defaultClauses:
                              quoteDetailsData?.viewQuoteFireSA?.clauses?.default &&
                              quoteDetailsData?.viewQuoteFireSA?.clauses?.default?.length > 0
                                ? quoteDetailsData?.viewQuoteFireSA?.clauses?.default
                                : [],
                            userDefinedClauses:
                              quoteDetailsData?.viewQuoteFireSA?.clauses?.userDefined &&
                              quoteDetailsData?.viewQuoteFireSA?.clauses?.userDefined?.length >
                                0
                                ? quoteDetailsData?.viewQuoteFireSA?.clauses?.userDefined
                                : [],
                          },
                          conditions: {
                            defaultConditions:
                              quoteDetailsData?.viewQuoteFireSA?.conditions?.default &&
                              quoteDetailsData?.viewQuoteFireSA?.conditions?.default?.length > 0
                                ? quoteDetailsData?.viewQuoteFireSA?.conditions?.default
                                : [],
                            userDefinedConditions:
                              quoteDetailsData?.viewQuoteFireSA?.conditions?.userDefined &&
                              quoteDetailsData?.viewQuoteFireSA?.conditions?.userDefined
                                ?.length > 0
                                ? quoteDetailsData?.viewQuoteFireSA?.conditions?.userDefined
                                : [],
                          },
                          warranties: {
                            defaultWarranties:
                              quoteDetailsData?.viewQuoteFireSA?.warranties?.default &&
                              quoteDetailsData?.viewQuoteFireSA?.warranties?.default?.length > 0
                                ? quoteDetailsData?.viewQuoteFireSA?.warranties?.default
                                : [],
                            userDefinedWarranties:
                              quoteDetailsData?.viewQuoteFireSA?.warranties?.userDefined &&
                              quoteDetailsData?.viewQuoteFireSA?.warranties?.userDefined
                                ?.length > 0
                                ? quoteDetailsData?.viewQuoteFireSA?.warranties?.userDefined
                                : [],
                          },
                          exclusions: {
                            defaultExclusions:
                              quoteDetailsData?.viewQuoteFireSA?.exclusions?.default &&
                              quoteDetailsData?.viewQuoteFireSA?.exclusions?.default?.length > 0
                                ? quoteDetailsData?.viewQuoteFireSA?.exclusions?.default
                                : [],
                            userDefinedExclusions:
                              quoteDetailsData?.viewQuoteFireSA?.exclusions?.userDefined &&
                              quoteDetailsData?.viewQuoteFireSA?.exclusions?.userDefined
                                ?.length > 0
                                ? quoteDetailsData?.viewQuoteFireSA?.exclusions?.userDefined
                                : [],
                          },
                          underwriterComment: quoteDetailsData?.viewQuoteFireSA
                            ?.underwriterComment
                            ? quoteDetailsData?.viewQuoteFireSA?.underwriterComment
                            : '',
                        },
                      },
                      finalisedPremium: {
                        locationNetPremium:
                          quoteDetailsData?.viewQuoteFireSA
                            ?.finalisedPremium
                            ?.locNetPremiumlocNetPremium,
                        locationGST:
                          quoteDetailsData?.viewQuoteFireSA
                            ?.finalisedPremium?.locGST,
                        locationTotalPremium:
                          quoteDetailsData?.viewQuoteFireSA
                            ?.finalisedPremium?.locTotalPremium,
                        // stampDuty:
                        //   quoteDetailsData?.viewQuoteFireSA
                        //     ?.finalisedPremium?.stampDuty,
                      },
                    });
                    setCopyQuoteDisplay(false);
                    setMoveToPlans(true);
                    document.body.classList.remove(
                      'overflow-y-hidden',
                    );
                    window?.scrollTo({ top: 0, behavior: 'smooth' });
                  } catch (error) {
                    setCopying(false);
                    setCopyFromQuoteNumber('');
                    setAlerts([
                      {
                        id: Date.now() + Math.random(),
                        content: <Text>{error?.message}</Text>,
                        type: 'error',
                        disableClose: false,
                        timeout: 3000,
                      },
                    ]);
                  }
                }}
                size={'small'}
                className="flex items-center h-8 space-x-2 w-24"
                iconPre={
                  copying &&
                  copyFromQuoteNumber ===
                    row?.original?.quoteNumber ? (
                    <FontAwesomeIcon icon={faSpinner} spin={true} />
                  ) : undefined
                }
                iconPost={
                  copying &&
                  copyFromQuoteNumber ===
                    row?.original?.quoteNumber ? undefined : (
                    <FontAwesomeIcon icon={faChevronRight} />
                  )
                }
                disabled={copying}
              >
                {copying &&
                copyFromQuoteNumber === row?.original?.quoteNumber
                  ? 'Importing...'
                  : 'Import'}
              </Button>
            </Text>
          );
        },
      },
    ].filter((d) => d.Header !== undefined),
    grid: {
      repeat: 1,
      gap: 2,
      gapX: 2,
      gapY: 2,
      gridClassName: 'w-full',
      gridItemsArr:
        searchData?.length > 0
          ? searchData?.map((quoteData) => {
              return {
                id: `${quoteData?.quoteNumber}`,
                content: (
                  <Paper
                    className={`border-2 rounded p-4 min-h-full 
                    ${'hover:bg-neutral-light'}
                    `}
                  >
                    <Text
                      className={
                        'flex flex-col w-full justify-between h-full'
                      }
                    >
                      <Text
                        className={
                          'flex items-center justify-between h-full w-full'
                        }
                      >
                        <Text
                          className={
                            'font-semibold flex space-x-2 items-start w-full'
                          }
                        >
                          <Text
                            className={
                              'flex flex-col justify-start items-center'
                            }
                          >
                            <Text
                              severity={'info'}
                              className="border-2 w-14 flex flex-col items-center justify-center h-12 rounded-lg bg-white mr-1.5"
                            >
                              <FontAwesomeIcon
                                icon={faUserInjured}
                                size={'2x'}
                              />
                            </Text>
                          </Text>
                          <Text
                            className={
                              'flex flex-col h-full justify-between w-full'
                            }
                          >
                            <Text
                              severity={'info'}
                              className={
                                'font-semibold w-full flex space-x-2 justify-between'
                              }
                            >
                              <Text className={'font-semibold'}>
                                {quoteData?.quoteNumber},
                                {quoteData?.product?.productName}
                              </Text>
                              <Text className={'font-semibold'}>
                                {quoteData?.quoteStatus}
                              </Text>
                            </Text>
                            <Text
                              type={'label'}
                              className={
                                'w-full h-full mb-5 text-neutral-darker'
                              }
                            >
                              Saved on {quoteData?.quoteCreationDate}
                            </Text>
                            <Text className={'w-full h-full'}>
                              <Grid
                                repeat={4}
                                className={'w-full h-full'}
                              >
                                {quoteData?.customer?.indv_detail
                                  ?.full_name ||
                                quoteData?.customer?.org_detail
                                  ?.org_name ? (
                                  <GridItem
                                    colSpan={
                                      quoteData?.lob?.lobName
                                        ?.toString()
                                        ?.toLowerCase() === 'fire'
                                        ? 3
                                        : 1
                                    }
                                    className={
                                      'flex space-x-2 w-full justify-start'
                                    }
                                  >
                                    <Text>
                                      <FontAwesomeIcon
                                        icon={faUser}
                                      />
                                    </Text>
                                    <Text>
                                      {quoteData?.customer
                                        ?.indv_detail?.full_name ||
                                        quoteData?.customer
                                          ?.org_detail?.org_name}
                                    </Text>
                                  </GridItem>
                                ) : (
                                  ''
                                )}
                                {quoteData?.customer?.contact
                                  ?.mobile ? (
                                  <GridItem
                                    colSpan={1}
                                    className={
                                      'flex space-x-2 w-full'
                                    }
                                  >
                                    <Text>
                                      <FontAwesomeIcon
                                        icon={faMobileAlt}
                                      />
                                    </Text>
                                    <Text>
                                      {
                                        quoteData?.customer?.contact
                                          ?.mobile
                                      }
                                    </Text>
                                  </GridItem>
                                ) : (
                                  ''
                                )}
                                {quoteData?.customer?.contact
                                  ?.email ? (
                                  <GridItem
                                    colSpan={1}
                                    className={
                                      'flex space-x-2 w-full'
                                    }
                                  >
                                    <Text>
                                      <FontAwesomeIcon
                                        icon={faEnvelope}
                                      />
                                    </Text>
                                    <Text>
                                      {
                                        quoteData?.customer?.contact
                                          ?.email
                                      }
                                    </Text>
                                  </GridItem>
                                ) : (
                                  ''
                                )}
                                {
                                  <GridItem
                                    colSpan={1}
                                    className={
                                      'flex space-x-2 w-full justify-end'
                                    }
                                  >
                                    <Text className="flex justify-end">
                                      <Button
                                        type={'button'}
                                        flat={false}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const fetchQuoteDetails =
                                              fetchQuery({
                                                query:
                                                VIEW_QUOTE_FIRE_SA,
                                                fetchPolicy:
                                                  'network-only',
                                                variables: {
                                                  input: {
                                                    quoteNumber:
                                                      quoteData?.quoteNumber,
                                                  },
                                                },
                                              });
                                            const getFireQuoteVar =
                                              fetchQuery({
                                                query:
                                                  GET_FIRE_POLICY_VAR_DETAILS,
                                              });
                                            setAlerts([]);
                                            setCopyFromQuoteNumber(
                                              quoteData?.quoteNumber,
                                            );
                                            setCopying(true);
                                            const {
                                              data: quoteDetailsData,
                                            } =
                                              await fetchQuoteDetails;
                                            const {
                                              data: fireReactiveVarData,
                                            } = await getFireQuoteVar;
                                            const { fireQuote } =
                                              fireReactiveVarData;
                                            setCopying(false);
                                            updateFireQuote({
                                              burglaryCoverType:
                                                quoteDetailsData
                                                  ?.viewQuoteFireSA
                                                  ?.burglaryCoverType
                                                  ? `${quoteDetailsData?.viewQuoteFireSA?.burglaryCoverType}`
                                                  : '',
                                              burglaryCoverDetailsTab:
                                                {
                                                  burglaryFloaterCoverDetails:
                                                    {
                                                      stockSI:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.burglaryFloaterCoverDetails
                                                          ?.stockSI
                                                          ? `${quoteDetailsData?.viewQuoteFireSA?.burglaryFloaterCoverDetails?.stockSI}`
                                                          : '',
                                                    },
                                                  burglaryNonFloaterCoverDetails:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.burglaryNonFloaterCoverDetails,
                                                  theftCovers:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.theftCovers,
                                                  classOfConstruction:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.constructionType
                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.constructionType}`
                                                      : '',
                                                  securityProvision:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.securityFeatures
                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.securityFeatures}`
                                                      : '',
                                                  loading:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.loadingPct
                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.loadingPct}`
                                                      : '',
                                                  riiotsStrikesandotherMaliciousDamage:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.riiotsStrikesandotherMaliciousDamage,
                                                  firstLossBasisOpted:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.firstLossBasisOpted,
                                                  firstLossLimitPercent:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.firstLossLimitPercent,
                                                },
                                              burglaryPremiumData: {
                                                burlgaryNetPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.burlgaryNetPremium
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.burlgaryNetPremium}`
                                                    : '',
                                                burlgaryGST:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.burlgaryGST
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.burlgaryGST}`
                                                    : '',
                                                burlgaryTotalPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.burlgaryTotalPremium
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.burlgaryTotalPremium}`
                                                    : '',
                                                loadingPct:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.loadingPct
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.loadingPct}`
                                                    : '',
                                                discountPct:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.discountPct
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.discountPct}`
                                                    : '',
                                              },
                                              currentTab:
                                                quoteDetailsData
                                                  ?.viewQuoteFireSA
                                                  ?.isBundledWithBurglary
                                                  ? 3
                                                  : 2,
                                              captureMode: 'create',
                                              quotationType:
                                                quoteDetailsData
                                                  ?.viewQuoteFireSA
                                                  ?.quotationType,
                                              isPremiumCalculating: false,
                                              tabDetails: {
                                                customerTabDetails: {
                                                  customerDetails: {
                                                    customer: {},
                                                    customerMeta: {},

                                                    businessOfInsured:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.businessOfInsured
                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.businessOfInsured}`
                                                        : '',
                                                  },
                                                  previousClaimExperienceDetails:
                                                    {
                                                      isClaimMadeInPreviousYears:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.prevClaimExpDtls
                                                          ?.anyPrevClaimsExp ===
                                                        true
                                                          ? 'Yes'
                                                          : 'No',
                                                      claimInPreviousYearsInfo:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.prevClaimExpDtls
                                                          ?.prevClaimExpDesc
                                                          ? `${quoteDetailsData?.viewQuoteFireSA?.prevClaimExpDtls?.prevClaimExpDesc}`
                                                          : '',
                                                    },
                                                  policyTypeDetails: {
                                                    isInsuranceForKutchaConstruction:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.isKutchaConstruct ===
                                                      true
                                                        ? 'Yes'
                                                        : 'No',
                                                    isPolicyBundledForBurglary:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.isBundledWithBurglary
                                                        ? 'Yes'
                                                        : 'No',
                                                    policyOrCoverType:
                                                      {
                                                        code: quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.policyType
                                                          ? `${quoteDetailsData?.viewQuoteFireSA?.policyType}`
                                                          : '',
                                                        value:
                                                          quoteDetailsData
                                                            ?.viewQuoteFireSA
                                                            ?.policyType
                                                            ? `${quoteDetailsData?.viewQuoteFireSA?.policyType}`
                                                            : '',
                                                      },
                                                    tenure: {
                                                      code: quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.tenure
                                                        ?.code,
                                                      value:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.tenure
                                                          ?.value,
                                                    },
                                                    policyStartDate:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.policyStartDate
                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.policyStartDate}`
                                                        : '',
                                                    policyExpiryDate:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.policyExpiryDate
                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.policyExpiryDate}`
                                                        : '',
                                                  },
                                                },
                                                riskLocationTabDetails:
                                                  {
                                                    floaterType:
                                                      quoteDetailsData?.viewQuoteFireSA?.policyType
                                                        ?.toString()
                                                        ?.toLowerCase() ===
                                                      'floater'
                                                        ? {
                                                            occupancy:
                                                              {
                                                                code: quoteDetailsData
                                                                  ?.viewQuoteFireSA
                                                                  ?.riskLocations[0]
                                                                  ?.occupancyCodeValue
                                                                  ?.code
                                                                  ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.code}`
                                                                  : '',
                                                                value:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.value
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.value}`
                                                                    : '',
                                                                occupancySection:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.occupancySection
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.occupancySection}`
                                                                    : '',
                                                                occupancyRate:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.occupancyRate
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.occupancyRate}`
                                                                    : '',
                                                                riskCode:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskCode
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskCode}`
                                                                    : '',
                                                                rateCode:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.rateCode
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.rateCode}`
                                                                    : '',
                                                                riskGrade:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskGrade
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskGrade}`
                                                                    : '',
                                                                riskGradeDefinition:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskGradeDefinition
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskGradeDefinition}`
                                                                    : '',
                                                                riskPreference:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskPreference
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskPreference}`
                                                                    : '',
                                                                gicClass:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.gicClass
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.gicClass}`
                                                                    : '',
                                                                stfiRate:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.stfiRate
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.stfiRate}`
                                                                    : '',
                                                                rsmdRate:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.rsmdRate
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.rsmdRate}`
                                                                    : '',
                                                              },
                                                            riskLocationID:
                                                              quoteDetailsData
                                                                ?.viewQuoteFireSA
                                                                ?.riskLocations[0]
                                                                ?.riskLocationID,
                                                            floaterLocations:
                                                              quoteDetailsData
                                                                ?.viewQuoteFireSA
                                                                ?.riskLocations[0]
                                                                ?.floaterLocations
                                                                ?.length >
                                                              0
                                                                ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.floaterLocations?.map(
                                                                    (
                                                                      floaterLocation,
                                                                    ) => {
                                                                      return {
                                                                        pinCode:
                                                                          floaterLocation?.pin_code,
                                                                        state:
                                                                          floaterLocation?.state,
                                                                        city: floaterLocation?.city,
                                                                        district:
                                                                          floaterLocation?.district,
                                                                        eqZone:
                                                                          floaterLocation?.eqZone,
                                                                        addressLine1:
                                                                          floaterLocation?.address_line1,
                                                                        locationCode:
                                                                          floaterLocation?.locationCode,
                                                                        showEdit: true,
                                                                        // addressLine2: floaterLocation?.addressLine2,
                                                                        // addressLine3: floaterLocation?.addressLine3,
                                                                        // landmark: floaterLocation?.landmark,
                                                                      };
                                                                    },
                                                                  )
                                                                : [],
                                                            riskSumInsured:
                                                              {
                                                                stocksSI:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.stockSI,
                                                                stocksInProcessSI:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.stocksInProcessSI,
                                                                stocksOnFloaterBasis:
                                                                  'Yes',
                                                                maximumStockSIAtOneLocation:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.maxStockSIAtOneLoc,
                                                              },
                                                            fireExtinguisherAppliancesDetails:
                                                              quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.fireExtinguishApplisDtls?.map(
                                                                (
                                                                  fireExtinguisherDtl,
                                                                ) => {
                                                                  return {
                                                                    label:
                                                                      fireExtinguisherDtl?.applianceValue
                                                                        ? fireExtinguisherDtl?.applianceValue
                                                                        : '',
                                                                    value:
                                                                      fireExtinguisherDtl?.applianceCode
                                                                        ? fireExtinguisherDtl?.applianceCode
                                                                        : '',
                                                                    selected:
                                                                      fireExtinguisherDtl?.isSelected
                                                                        ? true
                                                                        : false,
                                                                  };
                                                                },
                                                              ),
                                                            otherDetails:
                                                              {
                                                                classOfConstruction:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.constructionClass
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.constructionClass}`
                                                                    : '',
                                                                distanceFromWaterbody:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.waterbodyDistance
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.waterbodyDistance}`
                                                                    : '',
                                                                securityProvisions:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.securityProvisions
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.securityProvisions}`
                                                                    : '',
                                                              },
                                                            loadingAndDiscount:
                                                              {
                                                                discountPercentage:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.discountPct,
                                                                loadingPercentage:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.loadingPct,
                                                              },
                                                            previousClaimDetails:
                                                              {
                                                                claimExperience:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.prevClaimExpDesc,
                                                              },
                                                            locationPremium:
                                                              {
                                                                locationNetPremium:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.locationNetPremium,
                                                                locationGST:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.locationGST,
                                                                locationTotalPremium:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.locationTotalPremium,
                                                                stampDuty:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.stampDuty,
                                                              },
                                                          }
                                                        : {
                                                            occupancy:
                                                              {
                                                                code: '',
                                                                value:
                                                                  '',
                                                                occupancySection:
                                                                  '',
                                                                occupancyRate:
                                                                  '',
                                                                riskCode:
                                                                  '',
                                                                rateCode:
                                                                  '',
                                                                riskGrade:
                                                                  '',
                                                                riskGradeDefinition:
                                                                  '',
                                                                riskPreference:
                                                                  '',
                                                                gicClass:
                                                                  '',
                                                                stfiRate:
                                                                  '',
                                                                rsmdRate:
                                                                  '',
                                                              },
                                                            riskLocationID:
                                                              '',
                                                            floaterLocations:
                                                              [],
                                                            riskSumInsured:
                                                              {
                                                                stocksSI:
                                                                  '',
                                                                stocksInProcessSI:
                                                                  '',
                                                                stocksOnFloaterBasis:
                                                                  '',
                                                                maximumStockSIAtOneLocation:
                                                                  '',
                                                              },
                                                            fireExtinguisherAppliancesDetails:
                                                              [],
                                                            otherDetails:
                                                              {
                                                                classOfConstruction:
                                                                  '',
                                                                distanceFromWaterbody:
                                                                  '',
                                                                securityProvisions:
                                                                  '',
                                                              },
                                                            loadingAndDiscount:
                                                              {
                                                                discountPercentage:
                                                                  '',
                                                                loadingPercentage:
                                                                  '',
                                                              },
                                                            previousClaimDetails:
                                                              {
                                                                claimExperience:
                                                                  '',
                                                              },
                                                            locationPremium:
                                                              {
                                                                locationNetPremium:
                                                                  '',
                                                                locationGST:
                                                                  '',
                                                                locationTotalPremium:
                                                                  '',
                                                                stampDuty:
                                                                  '',
                                                              },
                                                          },
                                                    nonFloaterType:
                                                      quoteDetailsData?.viewQuoteFireSA?.policyType
                                                        ?.toString()
                                                        ?.toLowerCase() ===
                                                      'non floater'
                                                        ? quoteDetailsData?.viewQuoteFireSA?.riskLocations?.map(
                                                            (
                                                              _,
                                                              idx,
                                                            ) => {
                                                              return {
                                                                riskLocationID:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[
                                                                    idx
                                                                  ]
                                                                    ?.riskLocationID,
                                                                isDeleted: false,
                                                                occupancy:
                                                                  {
                                                                    code: quoteDetailsData
                                                                      ?.viewQuoteFireSA
                                                                      ?.riskLocations[
                                                                      idx
                                                                    ]
                                                                      ?.occupancyCodeValue
                                                                      ?.code
                                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.code}`
                                                                      : '',
                                                                    value:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.value
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.value}`
                                                                        : '',
                                                                    occupancySection:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.occupancySection
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.occupancySection}`
                                                                        : '',
                                                                    occupancyRate:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.occupancyRate
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.occupancyRate}`
                                                                        : '',
                                                                    riskCode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskCode
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskCode}`
                                                                        : '',
                                                                    rateCode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.rateCode
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.rateCode}`
                                                                        : '',
                                                                    riskGrade:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskGrade
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskGrade}`
                                                                        : '',
                                                                    riskGradeDefinition:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskGradeDefinition
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskGradeDefinition}`
                                                                        : '',
                                                                    riskPreference:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskPreference
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskPreference}`
                                                                        : '',
                                                                    gicClass:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.gicClass
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.gicClass}`
                                                                        : '',
                                                                    stfiRate:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.stfiRate
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.stfiRate}`
                                                                        : '',
                                                                    rsmdRate:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.rsmdRate
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.rsmdRate}`
                                                                        : '',
                                                                  },
                                                                isLocationSaved: true,
                                                                riskSumInsured:
                                                                  {
                                                                    buildingSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.buildingSI,
                                                                    plantAndMachinerySI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.plantMachinerySI,
                                                                    furnitureFixtureAndFittingsSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.furnitureFixtureSI,
                                                                    rawMaterialSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.rawMaterialSI,
                                                                    otherContentsSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.otherContentsSI,
                                                                  },
                                                                stock:
                                                                  {
                                                                    stocksSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.stockSI,
                                                                    stocksInProcessSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.stocksInProcessSI,
                                                                    stocksOnFloaterBasis:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.stockOnFloaterBasis ===
                                                                      true
                                                                        ? 'Yes'
                                                                        : 'No',
                                                                    maximumStockSIAtOneLocation:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.maxStockSIAtOneLoc,
                                                                  },
                                                                locationSumInsured:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[
                                                                    idx
                                                                  ]
                                                                    ?.locationSI,
                                                                fireExtinguisherAppliancesDetails:
                                                                  quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                                                    idx
                                                                  ]?.fireExtinguishApplisDtls?.map(
                                                                    (
                                                                      fireExtinguisherDtl,
                                                                    ) => {
                                                                      return {
                                                                        label:
                                                                          fireExtinguisherDtl?.applianceValue
                                                                            ? fireExtinguisherDtl?.applianceValue
                                                                            : '',
                                                                        value:
                                                                          fireExtinguisherDtl?.applianceCode
                                                                            ? fireExtinguisherDtl?.applianceCode
                                                                            : '',
                                                                        selected:
                                                                          fireExtinguisherDtl?.isSelected
                                                                            ? true
                                                                            : false,
                                                                      };
                                                                    },
                                                                  ),
                                                                otherDetails:
                                                                  {
                                                                    classOfConstruction:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.constructionClass
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.constructionClass}`
                                                                        : '',
                                                                    distanceFromWaterbody:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.waterbodyDistance
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.waterbodyDistance}`
                                                                        : '',
                                                                    securityProvisions:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.securityProvisions
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.securityProvisions}`
                                                                        : '',
                                                                  },
                                                                loadingAndDiscount:
                                                                  {
                                                                    discountPercentage:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.discountPct,
                                                                    loadingPercentage:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.loadingPct,
                                                                  },
                                                                locationPremium:
                                                                  {
                                                                    locationNetPremium:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.locationNetPremium,
                                                                    locationGST:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.locationGST,
                                                                    locationTotalPremium:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.locationTotalPremium,
                                                                    stampDuty:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.stampDuty,
                                                                  },
                                                                previousClaimDetails:
                                                                  {
                                                                    claimExperience:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.prevClaimExpDesc,
                                                                  },
                                                                addressDetails:
                                                                  {
                                                                    addressLine1:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.address_line1
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.address_line1}`
                                                                        : '',
                                                                    pincode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.pin_code
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.pin_code}`
                                                                        : '',
                                                                    state:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.state
                                                                        ? quoteDetailsData
                                                                            ?.viewQuoteFireSA
                                                                            ?.riskLocations[
                                                                            idx
                                                                          ]
                                                                            ?.riskLocAddress
                                                                            ?.state
                                                                        : '',
                                                                    eqZone:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.eqZone
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.eqZone}`
                                                                        : '',
                                                                    district:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.district
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.district}`
                                                                        : '',
                                                                    isSamePremise:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.isSamePremises
                                                                        ? true
                                                                        : false,
                                                                    locationCode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.locationCode
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.locationCode}`
                                                                        : '',
                                                                    locationDesc:
                                                                      '',
                                                                  },
                                                                stockLocations:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[
                                                                    idx
                                                                  ]
                                                                    ?.stockOnFloaterBasis ===
                                                                  true
                                                                    ? quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.floaterLocations
                                                                        ?.length >
                                                                      0
                                                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                                                          idx
                                                                        ]?.floaterLocations?.map(
                                                                          (
                                                                            floaterLocation,
                                                                          ) => {
                                                                            return {
                                                                              isDeleted: false,
                                                                              pincode:
                                                                                floaterLocation?.pin_code,
                                                                              state:
                                                                                floaterLocation?.state,
                                                                              city: floaterLocation?.city,
                                                                              district:
                                                                                floaterLocation?.district,
                                                                              eqZone:
                                                                                floaterLocation?.eqZone,
                                                                              addressLine1:
                                                                                floaterLocation?.address_line1,
                                                                              locationCode:
                                                                                floaterLocation?.locationCode,
                                                                            };
                                                                          },
                                                                        )
                                                                      : []
                                                                    : [],
                                                              };
                                                            },
                                                          )
                                                        : [],
                                                  },
                                                additionalTabDetails:
                                                  {
                                                    clauses: {
                                                      defaultClauses:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.clauses
                                                              ?.default
                                                          : [],
                                                      userDefinedClauses:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.clauses
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    conditions: {
                                                      defaultConditions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.conditions
                                                              ?.default
                                                          : [],
                                                      userDefinedConditions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.conditions
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    warranties: {
                                                      defaultWarranties:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.warranties
                                                              ?.default
                                                          : [],
                                                      userDefinedWarranties:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.warranties
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    exclusions: {
                                                      defaultExclusions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.exclusions
                                                              ?.default
                                                          : [],
                                                      userDefinedExclusions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.exclusions
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    underwriterComment:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.underwriterComment
                                                        ? quoteDetailsData
                                                            ?.viewQuoteFireSA
                                                            ?.underwriterComment
                                                        : '',
                                                  },
                                              },
                                              finalisedPremium: {
                                                locationNetPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.locNetPremiumlocNetPremium,
                                                locationGST:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.locGST,
                                                locationTotalPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.locTotalPremium,
                                                stampDuty:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.stampDuty,
                                              },
                                            });
                                            setCopyQuoteDisplay(
                                              false,
                                            );
                                            setMoveToPlans(true);
                                            document.body.classList.remove(
                                              'overflow-y-hidden',
                                            );
                                            window?.scrollTo({
                                              top: 0,
                                              behavior: 'smooth',
                                            });
                                          } catch (error) {
                                            setCopyFromQuoteNumber(
                                              '',
                                            );
                                            setCopying(false);
                                            setAlerts([
                                              {
                                                id:
                                                  Date.now() +
                                                  Math.random(),
                                                content: (
                                                  <Text>
                                                    {error?.message}
                                                  </Text>
                                                ),
                                                type: 'error',
                                                disableClose: false,
                                                timeout: 3000,
                                              },
                                            ]);
                                          }
                                        }}
                                        size={'small'}
                                        className="flex items-center h-8 space-x-2"
                                        iconPre={
                                          copying &&
                                          copyFromQuoteNumber ===
                                            quoteData?.quoteNumber ? (
                                            <FontAwesomeIcon
                                              icon={faSpinner}
                                              spin={true}
                                            />
                                          ) : undefined
                                        }
                                        iconPost={
                                          copying &&
                                          copyFromQuoteNumber ===
                                            quoteData?.quoteNumber ? undefined : (
                                            <FontAwesomeIcon
                                              icon={faChevronRight}
                                            />
                                          )
                                        }
                                        disabled={copying}
                                      >
                                        {copying &&
                                        copyFromQuoteNumber ===
                                          quoteData?.quoteNumber
                                          ? 'Importing...'
                                          : 'Import'}
                                      </Button>
                                    </Text>
                                  </GridItem>
                                }
                              </Grid>
                            </Text>
                          </Text>
                        </Text>
                      </Text>
                    </Text>
                  </Paper>
                ),
                colSpan: 1,
              };
            })
          : [],
    },
    card: {
      gap: 2,
      gapX: 2,
      gapY: 2,
      cardClassName: 'w-full',
      cardItemsArr:
        searchData?.length > 0
          ? searchData?.map((quoteData) => {
              return {
                id: `${quoteData?.quoteNumber}`,
                content: (
                  <Paper
                    className={`border-2 rounded p-4 min-h-full ${
                      //   cardSummaryDetail?.quoteNumber
                      //     .toString()
                      //     ?.toLowerCase() ===
                      //   quoteData?.quoteNumber
                      //     ?.toString()
                      //     ?.toLowerCase()
                      //     ? 'bg-neutral'
                      //     :
                      'hover:bg-neutral-light'
                    }`}
                  >
                    <Text
                      className={
                        'flex flex-col w-full justify-between h-full'
                      }
                    >
                      <Text
                        className={
                          'flex items-center justify-between h-full w-full'
                        }
                      >
                        <Text
                          className={
                            'font-semibold flex space-x-2 items-start w-full'
                          }
                        >
                          <Text
                            className={
                              'flex flex-col h-full justify-between w-full'
                            }
                          >
                            <Text
                              severity={'info'}
                              className={
                                'font-semibold w-full flex space-x-2 items-center justify-between'
                              }
                            >
                              <Text className={'font-semibold'}>
                                {quoteData?.quoteNumber},
                                {quoteData?.product?.productName}
                              </Text>
                              <Text className={'font-semibold'}>
                                {quoteData?.quoteStatus}
                              </Text>
                            </Text>
                            <Text
                              type={'label'}
                              className={
                                'w-full h-full mb-5 text-neutral-darker'
                              }
                            >
                              Saved on {quoteData?.quoteCreationDate}
                            </Text>
                            <Text className={'w-full h-full'}>
                              <Grid
                                repeat={2}
                                className={'w-full h-full'}
                              >
                                {quoteData?.customer?.indv_detail
                                  ?.full_name ||
                                quoteData?.customer?.org_detail
                                  ?.org_name ? (
                                  <GridItem
                                    colSpan={1}
                                    className={
                                      'flex space-x-2 w-full justify-start'
                                    }
                                  >
                                    <Text type={'label'}>
                                      <FontAwesomeIcon
                                        icon={faUser}
                                      />
                                    </Text>
                                    <Text type={'label'}>
                                      {quoteData?.customer
                                        ?.indv_detail?.full_name ||
                                        quoteData?.customer
                                          ?.org_detail?.org_name}
                                    </Text>
                                  </GridItem>
                                ) : (
                                  ''
                                )}
                                {quoteData?.customer?.contact
                                  ?.mobile ? (
                                  <GridItem
                                    colSpan={1}
                                    className={
                                      screenType === 'mobile'
                                        ? 'flex space-x-2 w-full justify-end'
                                        : 'flex space-x-2 w-full justify-start'
                                    }
                                  >
                                    <Text type={'label'}>
                                      <FontAwesomeIcon
                                        icon={faMobileAlt}
                                      />
                                    </Text>
                                    <Text type={'label'}>
                                      {
                                        quoteData?.customer?.contact
                                          ?.mobile
                                      }
                                    </Text>
                                  </GridItem>
                                ) : (
                                  ''
                                )}
                                {quoteData?.customer?.contact
                                  ?.email ? (
                                  <GridItem
                                    colSpan={1}
                                    className={
                                      'flex space-x-2 w-full break-all'
                                    }
                                  >
                                    <Text type={'label'}>
                                      <FontAwesomeIcon
                                        icon={faEnvelope}
                                      />
                                    </Text>
                                    <Text
                                      type={'label'}
                                      className={'break-all'}
                                    >
                                      {
                                        quoteData?.customer?.contact
                                          ?.email
                                      }
                                    </Text>
                                  </GridItem>
                                ) : (
                                  ''
                                )}
                                {
                                  <GridItem
                                    colSpan={1}
                                    className={
                                      'flex space-x-2 w-full justify-end'
                                    }
                                  >
                                    <Text className="flex justify-end">
                                      <Button
                                        type={'button'}
                                        flat={false}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const fetchQuoteDetails =
                                              fetchQuery({
                                                query:
                                                VIEW_QUOTE_FIRE_SA,
                                                variables: {
                                                  input: {
                                                    quoteNumber:
                                                      quoteData?.quoteNumber,
                                                  },
                                                },
                                              });
                                            const getFireQuoteVar =
                                              fetchQuery({
                                                query:
                                                  GET_FIRE_POLICY_VAR_DETAILS,
                                              });
                                            setAlerts([]);
                                            setCopyFromQuoteNumber(
                                              quoteData?.quoteNumber,
                                            );
                                            setCopying(true);
                                            const {
                                              data: quoteDetailsData,
                                            } =
                                              await fetchQuoteDetails;
                                            const {
                                              data: fireReactiveVarData,
                                            } = await getFireQuoteVar;
                                            const { ipaQuote } =
                                              fireReactiveVarData;
                                            setCopying(false);
                                            updateFireQuote({
                                              burglaryCoverType:
                                                quoteDetailsData
                                                  ?.viewQuoteFireSA
                                                  ?.burglaryCoverType
                                                  ? `${quoteDetailsData?.viewQuoteFireSA?.burglaryCoverType}`
                                                  : '',
                                              burglaryCoverDetailsTab:
                                                {
                                                  burglaryFloaterCoverDetails:
                                                    {
                                                      stockSI:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.burglaryFloaterCoverDetails
                                                          ?.stockSI
                                                          ? `${quoteDetailsData?.viewQuoteFireSA?.burglaryFloaterCoverDetails?.stockSI}`
                                                          : '',
                                                    },
                                                  burglaryNonFloaterCoverDetails:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.burglaryNonFloaterCoverDetails,
                                                  theftCovers:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.theftCovers,
                                                  classOfConstruction:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.constructionType
                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.constructionType}`
                                                      : '',
                                                  securityProvision:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.securityFeatures
                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.securityFeatures}`
                                                      : '',
                                                  loading:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.loadingPct
                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.loadingPct}`
                                                      : '',
                                                  riiotsStrikesandotherMaliciousDamage:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.riiotsStrikesandotherMaliciousDamage,
                                                  firstLossBasisOpted:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.firstLossBasisOpted,
                                                  firstLossLimitPercent:
                                                    quoteDetailsData
                                                      ?.viewQuoteFireSA
                                                      ?.firstLossLimitPercent,
                                                },
                                              burglaryPremiumData: {
                                                burlgaryNetPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.burlgaryNetPremium
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.burlgaryNetPremium}`
                                                    : '',
                                                burlgaryGST:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.burlgaryGST
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.burlgaryGST}`
                                                    : '',
                                                burlgaryTotalPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.burlgaryTotalPremium
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.burlgaryTotalPremium}`
                                                    : '',
                                                loadingPct:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.loadingPct
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.loadingPct}`
                                                    : '',
                                                discountPct:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.premiumBurglary
                                                    ?.discountPct
                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.premiumBurglary?.discountPct}`
                                                    : '',
                                              },
                                              currentTab:
                                                quoteDetailsData
                                                  ?.viewQuoteFireSA
                                                  ?.isBundledWithBurglary
                                                  ? 3
                                                  : 2,
                                              captureMode: 'create',
                                              quotationType:
                                                quoteDetailsData
                                                  ?.viewQuoteFireSA
                                                  ?.quotationType,
                                              isPremiumCalculating: false,
                                              tabDetails: {
                                                customerTabDetails: {
                                                  customerDetails: {
                                                    customer: {},
                                                    customerMeta: {},
                                                    businessOfInsured:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.businessOfInsured
                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.businessOfInsured}`
                                                        : '',
                                                  },
                                                  previousClaimExperienceDetails:
                                                    {
                                                      isClaimMadeInPreviousYears:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.prevClaimExpDtls
                                                          ?.anyPrevClaimsExp ===
                                                        true
                                                          ? 'Yes'
                                                          : 'No',
                                                      claimInPreviousYearsInfo:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.prevClaimExpDtls
                                                          ?.prevClaimExpDesc
                                                          ? `${quoteDetailsData?.viewQuoteFireSA?.prevClaimExpDtls?.prevClaimExpDesc}`
                                                          : '',
                                                    },
                                                  policyTypeDetails: {
                                                    isInsuranceForKutchaConstruction:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.isKutchaConstruct ===
                                                      true
                                                        ? 'Yes'
                                                        : 'No',
                                                    isPolicyBundledForBurglary:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.isBundledWithBurglary
                                                        ? 'Yes'
                                                        : 'No',
                                                    policyOrCoverType:
                                                      {
                                                        code: quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.policyType
                                                          ? `${quoteDetailsData?.viewQuoteFireSA?.policyType}`
                                                          : '',
                                                        value:
                                                          quoteDetailsData
                                                            ?.viewQuoteFireSA
                                                            ?.policyType
                                                            ? `${quoteDetailsData?.viewQuoteFireSA?.policyType}`
                                                            : '',
                                                      },
                                                    tenure: {
                                                      code: quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.tenure
                                                        ?.code,
                                                      value:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.tenure
                                                          ?.value,
                                                    },
                                                    policyStartDate:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.policyStartDate
                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.policyStartDate}`
                                                        : '',
                                                    policyExpiryDate:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.policyExpiryDate
                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.policyExpiryDate}`
                                                        : '',
                                                  },
                                                },
                                                riskLocationTabDetails:
                                                  {
                                                    floaterType:
                                                      quoteDetailsData?.viewQuoteFireSA?.policyType
                                                        ?.toString()
                                                        ?.toLowerCase() ===
                                                      'floater'
                                                        ? {
                                                            occupancy:
                                                              {
                                                                code: quoteDetailsData
                                                                  ?.viewQuoteFireSA
                                                                  ?.riskLocations[0]
                                                                  ?.occupancyCodeValue
                                                                  ?.code
                                                                  ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.code}`
                                                                  : '',
                                                                value:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.value
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.value}`
                                                                    : '',
                                                                occupancySection:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.occupancySection
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.occupancySection}`
                                                                    : '',
                                                                occupancyRate:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.occupancyRate
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.occupancyRate}`
                                                                    : '',
                                                                riskCode:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskCode
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskCode}`
                                                                    : '',
                                                                rateCode:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.rateCode
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.rateCode}`
                                                                    : '',
                                                                riskGrade:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskGrade
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskGrade}`
                                                                    : '',
                                                                riskGradeDefinition:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskGradeDefinition
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskGradeDefinition}`
                                                                    : '',
                                                                riskPreference:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.riskPreference
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.riskPreference}`
                                                                    : '',
                                                                gicClass:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.gicClass
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.gicClass}`
                                                                    : '',
                                                                stfiRate:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.stfiRate
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.stfiRate}`
                                                                    : '',
                                                                rsmdRate:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.occupancyCodeValue
                                                                    ?.rsmdRate
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.occupancyCodeValue?.rsmdRate}`
                                                                    : '',
                                                              },
                                                            riskLocationID:
                                                              quoteDetailsData
                                                                ?.viewQuoteFireSA
                                                                ?.riskLocations[0]
                                                                ?.riskLocationID,
                                                            floaterLocations:
                                                              quoteDetailsData
                                                                ?.viewQuoteFireSA
                                                                ?.riskLocations[0]
                                                                ?.floaterLocations
                                                                ?.length >
                                                              0
                                                                ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.floaterLocations?.map(
                                                                    (
                                                                      floaterLocation,
                                                                    ) => {
                                                                      return {
                                                                        pinCode:
                                                                          floaterLocation?.pin_code,
                                                                        state:
                                                                          floaterLocation?.state,
                                                                        city: floaterLocation?.city,
                                                                        district:
                                                                          floaterLocation?.district,
                                                                        eqZone:
                                                                          floaterLocation?.eqZone,
                                                                        addressLine1:
                                                                          floaterLocation?.address_line1,
                                                                        locationCode:
                                                                          floaterLocation?.locationCode,
                                                                        showEdit: true,
                                                                        // addressLine2: floaterLocation?.addressLine2,
                                                                        // addressLine3: floaterLocation?.addressLine3,
                                                                        // landmark: floaterLocation?.landmark,
                                                                      };
                                                                    },
                                                                  )
                                                                : [],
                                                            riskSumInsured:
                                                              {
                                                                stocksSI:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.stockSI,
                                                                stocksInProcessSI:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.stocksInProcessSI,
                                                                stocksOnFloaterBasis:
                                                                  'Yes',
                                                                maximumStockSIAtOneLocation:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.maxStockSIAtOneLoc,
                                                              },
                                                            fireExtinguisherAppliancesDetails:
                                                              quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.fireExtinguishApplisDtls?.map(
                                                                (
                                                                  fireExtinguisherDtl,
                                                                ) => {
                                                                  return {
                                                                    label:
                                                                      fireExtinguisherDtl?.applianceValue
                                                                        ? fireExtinguisherDtl?.applianceValue
                                                                        : '',
                                                                    value:
                                                                      fireExtinguisherDtl?.applianceCode
                                                                        ? fireExtinguisherDtl?.applianceCode
                                                                        : '',
                                                                    selected:
                                                                      fireExtinguisherDtl?.isSelected
                                                                        ? true
                                                                        : false,
                                                                  };
                                                                },
                                                              ),
                                                            otherDetails:
                                                              {
                                                                classOfConstruction:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.constructionClass
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.constructionClass}`
                                                                    : '',
                                                                distanceFromWaterbody:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.waterbodyDistance
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.waterbodyDistance}`
                                                                    : '',
                                                                securityProvisions:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.securityProvisions
                                                                    ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[0]?.securityProvisions}`
                                                                    : '',
                                                              },
                                                            loadingAndDiscount:
                                                              {
                                                                discountPercentage:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.discountPct,
                                                                loadingPercentage:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.loadingPct,
                                                              },
                                                            previousClaimDetails:
                                                              {
                                                                claimExperience:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.prevClaimExpDesc,
                                                              },
                                                            locationPremium:
                                                              {
                                                                locationNetPremium:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.locationNetPremium,
                                                                locationGST:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.locationGST,
                                                                locationTotalPremium:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.locationTotalPremium,
                                                                stampDuty:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[0]
                                                                    ?.premium
                                                                    ?.stampDuty,
                                                              },
                                                          }
                                                        : {
                                                            occupancy:
                                                              {
                                                                code: '',
                                                                value:
                                                                  '',
                                                                occupancySection:
                                                                  '',
                                                                occupancyRate:
                                                                  '',
                                                                riskCode:
                                                                  '',
                                                                rateCode:
                                                                  '',
                                                                riskGrade:
                                                                  '',
                                                                riskGradeDefinition:
                                                                  '',
                                                                riskPreference:
                                                                  '',
                                                                gicClass:
                                                                  '',
                                                                stfiRate:
                                                                  '',
                                                                rsmdRate:
                                                                  '',
                                                              },
                                                            riskLocationID:
                                                              '',
                                                            floaterLocations:
                                                              [],
                                                            riskSumInsured:
                                                              {
                                                                stocksSI:
                                                                  '',
                                                                stocksInProcessSI:
                                                                  '',
                                                                stocksOnFloaterBasis:
                                                                  '',
                                                                maximumStockSIAtOneLocation:
                                                                  '',
                                                              },
                                                            fireExtinguisherAppliancesDetails:
                                                              [],
                                                            otherDetails:
                                                              {
                                                                classOfConstruction:
                                                                  '',
                                                                distanceFromWaterbody:
                                                                  '',
                                                                securityProvisions:
                                                                  '',
                                                              },
                                                            loadingAndDiscount:
                                                              {
                                                                discountPercentage:
                                                                  '',
                                                                loadingPercentage:
                                                                  '',
                                                              },
                                                            previousClaimDetails:
                                                              {
                                                                claimExperience:
                                                                  '',
                                                              },
                                                            locationPremium:
                                                              {
                                                                locationNetPremium:
                                                                  '',
                                                                locationGST:
                                                                  '',
                                                                locationTotalPremium:
                                                                  '',
                                                                stampDuty:
                                                                  '',
                                                              },
                                                          },
                                                    nonFloaterType:
                                                      quoteDetailsData?.viewQuoteFireSA?.policyType
                                                        ?.toString()
                                                        ?.toLowerCase() ===
                                                      'non floater'
                                                        ? quoteDetailsData?.viewQuoteFireSA?.riskLocations?.map(
                                                            (
                                                              _,
                                                              idx,
                                                            ) => {
                                                              return {
                                                                riskLocationID:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[
                                                                    idx
                                                                  ]
                                                                    ?.riskLocationID,
                                                                isDeleted: false,
                                                                occupancy:
                                                                  {
                                                                    code: quoteDetailsData
                                                                      ?.viewQuoteFireSA
                                                                      ?.riskLocations[
                                                                      idx
                                                                    ]
                                                                      ?.occupancyCodeValue
                                                                      ?.code
                                                                      ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.code}`
                                                                      : '',
                                                                    value:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.value
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.value}`
                                                                        : '',
                                                                    occupancySection:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.occupancySection
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.occupancySection}`
                                                                        : '',
                                                                    occupancyRate:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.occupancyRate
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.occupancyRate}`
                                                                        : '',
                                                                    riskCode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskCode
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskCode}`
                                                                        : '',
                                                                    rateCode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.rateCode
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.rateCode}`
                                                                        : '',
                                                                    riskGrade:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskGrade
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskGrade}`
                                                                        : '',
                                                                    riskGradeDefinition:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskGradeDefinition
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskGradeDefinition}`
                                                                        : '',
                                                                    riskPreference:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.riskPreference
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.riskPreference}`
                                                                        : '',
                                                                    gicClass:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.gicClass
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.gicClass}`
                                                                        : '',
                                                                    stfiRate:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.stfiRate
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.stfiRate}`
                                                                        : '',
                                                                    rsmdRate:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.occupancyCodeValue
                                                                        ?.rsmdRate
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.occupancyCodeValue?.rsmdRate}`
                                                                        : '',
                                                                  },
                                                                isLocationSaved: true,
                                                                riskSumInsured:
                                                                  {
                                                                    buildingSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.buildingSI,
                                                                    plantAndMachinerySI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.plantMachinerySI,
                                                                    furnitureFixtureAndFittingsSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.furnitureFixtureSI,
                                                                    rawMaterialSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.rawMaterialSI,
                                                                    otherContentsSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.otherContentsSI,
                                                                  },
                                                                stock:
                                                                  {
                                                                    stocksSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.stockSI,
                                                                    stocksInProcessSI:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.stocksInProcessSI,
                                                                    stocksOnFloaterBasis:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.stockOnFloaterBasis ===
                                                                      true
                                                                        ? 'Yes'
                                                                        : 'No',
                                                                    maximumStockSIAtOneLocation:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.maxStockSIAtOneLoc,
                                                                  },
                                                                locationSumInsured:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[
                                                                    idx
                                                                  ]
                                                                    ?.locationSI,
                                                                fireExtinguisherAppliancesDetails:
                                                                  quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                                                    idx
                                                                  ]?.fireExtinguishApplisDtls?.map(
                                                                    (
                                                                      fireExtinguisherDtl,
                                                                    ) => {
                                                                      return {
                                                                        label:
                                                                          fireExtinguisherDtl?.applianceValue
                                                                            ? fireExtinguisherDtl?.applianceValue
                                                                            : '',
                                                                        value:
                                                                          fireExtinguisherDtl?.applianceCode
                                                                            ? fireExtinguisherDtl?.applianceCode
                                                                            : '',
                                                                        selected:
                                                                          fireExtinguisherDtl?.isSelected
                                                                            ? true
                                                                            : false,
                                                                      };
                                                                    },
                                                                  ),
                                                                otherDetails:
                                                                  {
                                                                    classOfConstruction:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.constructionClass
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.constructionClass}`
                                                                        : '',
                                                                    distanceFromWaterbody:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.waterbodyDistance
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.waterbodyDistance}`
                                                                        : '',
                                                                    securityProvisions:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.securityProvisions
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.securityProvisions}`
                                                                        : '',
                                                                  },
                                                                loadingAndDiscount:
                                                                  {
                                                                    discountPercentage:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.discountPct,
                                                                    loadingPercentage:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.loadingPct,
                                                                  },
                                                                locationPremium:
                                                                  {
                                                                    locationNetPremium:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.locationNetPremium,
                                                                    locationGST:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.locationGST,
                                                                    locationTotalPremium:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.locationTotalPremium,
                                                                    stampDuty:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.premium
                                                                        ?.stampDuty,
                                                                  },
                                                                previousClaimDetails:
                                                                  {
                                                                    claimExperience:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.prevClaimExpDesc,
                                                                  },
                                                                addressDetails:
                                                                  {
                                                                    addressLine1:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.address_line1
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.address_line1}`
                                                                        : '',
                                                                    pincode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.pin_code
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.pin_code}`
                                                                        : '',
                                                                    state:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.state
                                                                        ? quoteDetailsData
                                                                            ?.viewQuoteFireSA
                                                                            ?.riskLocations[
                                                                            idx
                                                                          ]
                                                                            ?.riskLocAddress
                                                                            ?.state
                                                                        : '',
                                                                    eqZone:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.eqZone
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.eqZone}`
                                                                        : '',
                                                                    district:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.district
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.district}`
                                                                        : '',
                                                                    isSamePremise:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.isSamePremises
                                                                        ? true
                                                                        : false,
                                                                    locationCode:
                                                                      quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.riskLocAddress
                                                                        ?.locationCode
                                                                        ? `${quoteDetailsData?.viewQuoteFireSA?.riskLocations[idx]?.riskLocAddress?.locationCode}`
                                                                        : '',
                                                                    locationDesc:
                                                                      '',
                                                                  },
                                                                stockLocations:
                                                                  quoteDetailsData
                                                                    ?.viewQuoteFireSA
                                                                    ?.riskLocations[
                                                                    idx
                                                                  ]
                                                                    ?.stockOnFloaterBasis ===
                                                                  true
                                                                    ? quoteDetailsData
                                                                        ?.viewQuoteFireSA
                                                                        ?.riskLocations[
                                                                        idx
                                                                      ]
                                                                        ?.floaterLocations
                                                                        ?.length >
                                                                      0
                                                                      ? quoteDetailsData?.viewQuoteFireSA?.riskLocations[
                                                                          idx
                                                                        ]?.floaterLocations?.map(
                                                                          (
                                                                            floaterLocation,
                                                                          ) => {
                                                                            return {
                                                                              isDeleted: false,
                                                                              pincode:
                                                                                floaterLocation?.pin_code,
                                                                              state:
                                                                                floaterLocation?.state,
                                                                              city: floaterLocation?.city,
                                                                              district:
                                                                                floaterLocation?.district,
                                                                              eqZone:
                                                                                floaterLocation?.eqZone,
                                                                              addressLine1:
                                                                                floaterLocation?.address_line1,
                                                                              locationCode:
                                                                                floaterLocation?.locationCode,
                                                                            };
                                                                          },
                                                                        )
                                                                      : []
                                                                    : [],
                                                              };
                                                            },
                                                          )
                                                        : [],
                                                  },
                                                additionalTabDetails:
                                                  {
                                                    clauses: {
                                                      defaultClauses:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.clauses
                                                              ?.default
                                                          : [],
                                                      userDefinedClauses:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.clauses
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.clauses
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    conditions: {
                                                      defaultConditions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.conditions
                                                              ?.default
                                                          : [],
                                                      userDefinedConditions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.conditions
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.conditions
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    warranties: {
                                                      defaultWarranties:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.warranties
                                                              ?.default
                                                          : [],
                                                      userDefinedWarranties:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.warranties
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.warranties
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    exclusions: {
                                                      defaultExclusions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.default &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.default
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.exclusions
                                                              ?.default
                                                          : [],
                                                      userDefinedExclusions:
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.userDefined &&
                                                        quoteDetailsData
                                                          ?.viewQuoteFireSA
                                                          ?.exclusions
                                                          ?.userDefined
                                                          ?.length > 0
                                                          ? quoteDetailsData
                                                              ?.viewQuoteFireSA
                                                              ?.exclusions
                                                              ?.userDefined
                                                          : [],
                                                    },
                                                    underwriterComment:
                                                      quoteDetailsData
                                                        ?.viewQuoteFireSA
                                                        ?.underwriterComment
                                                        ? quoteDetailsData
                                                            ?.viewQuoteFireSA
                                                            ?.underwriterComment
                                                        : '',
                                                  },
                                              },
                                              finalisedPremium: {
                                                locationNetPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.locNetPremiumlocNetPremium,
                                                locationGST:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.locGST,
                                                locationTotalPremium:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.locTotalPremium,
                                                stampDuty:
                                                  quoteDetailsData
                                                    ?.viewQuoteFireSA
                                                    ?.finalisedPremium
                                                    ?.stampDuty,
                                              },
                                            });
                                            setCopyQuoteDisplay(
                                              false,
                                            );
                                            setMoveToPlans(true);
                                            document.body.classList.remove(
                                              'overflow-y-hidden',
                                            );
                                            window?.scrollTo({
                                              top: 0,
                                              behavior: 'smooth',
                                            });
                                          } catch (error) {
                                            setCopying(false);
                                            setCopyFromQuoteNumber(
                                              '',
                                            );
                                            setAlerts([
                                              {
                                                id:
                                                  Date.now() +
                                                  Math.random(),
                                                content: (
                                                  <Text>
                                                    {error?.message}
                                                  </Text>
                                                ),
                                                type: 'error',
                                                disableClose: false,
                                                timeout: 3000,
                                              },
                                            ]);
                                          }
                                        }}
                                        size={'small'}
                                        className="flex items-center h-8 space-x-2"
                                        iconPre={
                                          copying &&
                                          copyFromQuoteNumber ===
                                            quoteData?.quoteNumber ? (
                                            <FontAwesomeIcon
                                              icon={faSpinner}
                                              spin={true}
                                            />
                                          ) : undefined
                                        }
                                        iconPost={
                                          copying &&
                                          copyFromQuoteNumber ===
                                            quoteData?.quoteNumber ? undefined : (
                                            <FontAwesomeIcon
                                              icon={faChevronRight}
                                            />
                                          )
                                        }
                                        disabled={copying}
                                      >
                                        {copying &&
                                        copyFromQuoteNumber ===
                                          quoteData?.quoteNumber
                                          ? 'Importing...'
                                          : 'Import'}
                                      </Button>
                                    </Text>
                                  </GridItem>
                                }
                              </Grid>
                            </Text>
                          </Text>
                        </Text>
                      </Text>
                    </Text>
                  </Paper>
                ),
                colSpan: 1,
              };
            })
          : [],
    },
    isSortRequired: false,
    list: searchData.map((viewQuote) => {
      return {
        id: `${viewQuote.quoteNumber}`,
        title: `${viewQuote.quoteNumber}`,
        subtitle: (
          <div className="flex flex-col">
            <span>
              {viewQuote?.customer?.org_detail?.org_name
                ? viewQuote?.customer?.org_detail?.org_name
                : viewQuote?.customer?.indv_detail?.full_name
                ? viewQuote?.customer?.indv_detail?.full_name
                : [
                    viewQuote?.customer?.indv_detail?.first_name?.trim(),
                    viewQuote?.customer?.indv_detail?.middle_name?.trim(),
                    viewQuote?.customer?.indv_detail?.surname?.trim(),
                  ].join(' ')}
            </span>
            <span>
              {viewQuote?.quoteStatus}
              {' , '}
              {viewQuote?.quotationType}
            </span>
          </div>
        ),
        onClick: () => undefined,
        //   history.push(
        //     `/quote/saved/${viewQuote?.lob?.lobCode}/${viewQuote?.lob?.lobName}/${viewQuote?.product?.productCode}/${viewQuote?.product?.productName}/${viewQuote?.quoteNumber}`,
        //   ),
      };
    }),
  };

  const paginationInput = {
    offset,
    PAGE_SIZE,
    // onClickPrev: () => setCardSummaryDetail(undefined),
    // onClickNext: () => setCardSummaryDetail(undefined),
  };

  const onMessage = () => {
    return (
      <>
        {searchQuoteMeta?.resultCount > searchQuoteMeta?.maxRow && (
          <Text severity="warning" className="space-x-1">
            <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
            <span>
              Search results are truncated to first{' '}
              {searchQuoteMeta?.maxRow} records.{'\t'}Please refine
              your search to get more accurate quote data.
            </span>
          </Text>
        )}
      </>
    );
  };

  return (
    // <Card className="lg:h-full min-h-screen">
    <>
      {/* <SearchResults */}
      <SearchResultsAdvanced
        {...{
          searchInput,
          searchResults,
          paginationInput,
          onMessage,
          switchViewEnabled: true,
          firstRowClickRequired: false,
        }}
      />
      <div>
        <NotificationList {...{ items: alerts }} />
      </div>
    </>
    // </Card>
  );
}
